<div class="px-4">
  <ng-container *ngIf="loading(); else content">
    <div class="text-center" i18n="@@viewCategoryLoading">Loading...</div>
  </ng-container>

  <ng-template #content>
    <ng-container *ngIf="error(); else main">
      <div class="text-center text-red-500">{{ error() }}</div>
    </ng-container>

    <ng-template #main>
      <div *ngIf="category()" class="flex flex-col gap-4 pb-6">
        <h2 class="text-primary font-['Inter'] text-2xl font-semibold">{{ activeCategoryTitle() }}</h2>

        <!-- Filters Section -->
        <div class="mb-4 flex flex-col gap-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div class="grid grid-cols-4 gap-6">
            <!-- Product Name Search -->
            <div class="group relative">
              <label
                for="productName"
                class="absolute -top-2 z-10 bg-white px-1 text-[12px] font-semibold text-gray-700">
                Product Name
              </label>
              <div class="relative">
                <input
                  type="text"
                  id="productName"
                  [(ngModel)]="filters.productTitle"
                  placeholder="Product Name"
                  class="w-full rounded-[10px] border border-gray-400 bg-white py-2.5 text-[14px] text-gray-700 outline-none" />
              </div>
            </div>

            <div class="group relative">
              <label class="absolute -top-2 z-10 bg-white px-1 text-[12px] font-semibold text-gray-700">Category</label>
              <select
                [(ngModel)]="filters.categoryId"
                (ngModelChange)="onCategoryChange($event)"
                class="w-full rounded-[10px] border border-gray-400 py-2.5 text-[14px] text-gray-700">
                <option [ngValue]="undefined">Categories</option>
                <option *ngFor="let cat of categories()?.list" [ngValue]="cat.id">
                  {{ getLocalizedTitle(cat) }}
                </option>
              </select>
            </div>

            <div class="group relative">
              <label class="absolute -top-2 z-10 bg-white px-1 text-[12px] font-semibold text-gray-700">
                Sub Category
              </label>
              <select
                [(ngModel)]="filters.subCategoryId"
                [disabled]="!subCategories() || subCategories()?.list?.length === 0"
                class="w-full rounded-[10px] border border-gray-400 py-2.5 text-[14px] text-gray-700">
                <option [ngValue]="undefined">Sub Categories</option>
                <option *ngFor="let sub of subCategories()?.list" [ngValue]="sub.id">
                  {{ getLocalizedTitle(sub) }}
                </option>
              </select>
            </div>

            <!-- Company Filter -->
            <div class="group relative">
              <label
                for="manufacturingCompanyId"
                class="absolute -top-2 z-10 bg-white px-1 text-[12px] font-semibold text-gray-700">
                Company
              </label>
              <select
                id="manufacturingCompanyId"
                [(ngModel)]="filters.mfgCompanyId"
                class="w-full rounded-[10px] border border-gray-400 py-2.5 text-[14px] text-gray-700">
                <option [ngValue]="undefined">Default</option>
                <option *ngFor="let company of manufacturers()?.list" [ngValue]="company.id">
                  {{ getLocalizedTitle(company) }}
                </option>
              </select>
            </div>
          </div>
          <div class="grid grid-cols-2 gap-10">
            <div class="flex flex-col gap-3">
              <div class="flex items-center justify-between px-1">
                <label class="text-[14px] font-bold text-gray-800">People Price</label>
                <div class="flex items-center gap-1 text-[13px] font-bold text-gray-800" dir="ltr">
                  <span>EGP</span>
                  <span>{{ filters.customerPriceFrom }} - {{ filters.customerPriceTo }}</span>
                </div>
              </div>
              <div
                class="range-slider-container"
                [ngStyle]="getSliderTrackStyle(filters.customerPriceFrom || 0, filters.customerPriceTo || 0)">
                <input
                  type="range"
                  min="0"
                  max="1000"
                  step="10"
                  [(ngModel)]="filters.customerPriceFrom"
                  (input)="validateRange('people')"
                  class="range-input" />
                <input
                  type="range"
                  min="0"
                  max="1000"
                  step="10"
                  [(ngModel)]="filters.customerPriceTo"
                  (input)="validateRange('people')"
                  class="range-input" />
              </div>
            </div>

            <div class="flex flex-col gap-3">
              <div class="flex items-center justify-between px-1">
                <label class="text-[14px] font-bold text-gray-800">Pharmacy Price</label>
                <div class="flex items-center gap-1 text-[13px] font-bold text-gray-800" dir="ltr">
                  <span>EGP</span>
                  <span>{{ filters.businessPriceFrom }} - {{ filters.businessPriceTo }}</span>
                </div>
              </div>
              <div
                class="range-slider-container"
                [ngStyle]="getSliderTrackStyle(filters.businessPriceFrom || 0, filters.businessPriceTo || 0)">
                <input
                  type="range"
                  min="0"
                  max="1000"
                  step="10"
                  [(ngModel)]="filters.businessPriceFrom"
                  (input)="validateRange('pharmacy')"
                  class="range-input" />
                <input
                  type="range"
                  min="0"
                  max="1000"
                  step="10"
                  [(ngModel)]="filters.businessPriceTo"
                  (input)="validateRange('pharmacy')"
                  class="range-input" />
              </div>
            </div>
          </div>

          <!-- Price Filters and Sort -->
          <div class="mt-4 flex justify-between gap-4">
            <div class="flex flex-1 gap-4">
              <select [(ngModel)]="filters.orderBy" class="rounded-[10px] border border-gray-400 px-4 py-2">
                <option value="CREATION_DATE">Creation Date</option>
                <option value="LAST_MODIFICATION_DATE">Last Modified</option>
                <option value="CUSTOMER_PRICE">Customer Price</option>
                <option value="BUSINESS_PRICE">Business Price</option>
              </select>

              <button (click)="toggleSortOrder()" class="rounded-[10px] border border-gray-400 px-4 py-2">
                {{ getLocalizedSortOrder() }}
              </button>
            </div>
            <div class="flex gap-3">
              <button (click)="resetFilters()" class="border-primary text-primary rounded-[6px] border px-8 py-2">
                <span i18n="@@adminProductsResetButton">Clear</span>
              </button>
              <button (click)="applyFilters()" class="bg-primary rounded-[6px] px-8 py-2 text-white">Apply</button>
            </div>
          </div>
        </div>
        <div class="px-4">
          @if (loading()) {
            <div class="py-10 text-center">Loading...</div>
          } @else if (category()) {
            <div class="flex flex-col gap-4 pb-6">
              <div class="grid w-full grid-cols-4 gap-4 px-2">
                @for (product of paginatedProducts(); track product.id) {
                  <div
                    class="group relative flex flex-col overflow-hidden rounded-xl border border-gray-100 bg-white transition-all hover:shadow-lg">
                    <div class="bg-surface-subtle relative flex h-52 w-full items-center justify-center">
                      <!-- @if (product.discount) {
                <div class="absolute top-0 left-0 z-10 rounded-br-xl bg-[#C42727] px-3 py-1">
                  <span class="text-[11px] font-bold text-white">{{ product.discount }}% off</span>
                </div>
              } -->

                      <div class="absolute top-3 right-3 z-10 flex flex-col gap-2">
                        <button
                          (click)="onAddToWishlist(product)"
                          class="hover-text-primary flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-400 shadow-sm transition-all">
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.5">
                            <path
                              d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                          </svg>
                        </button>
                        <button
                          (click)="onAddToCart(product)"
                          class="hover-text-primary flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-400 shadow-sm transition-all">
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.5">
                            <path
                              d="M1 2h3l3 11m0 0h12l3-9M7 13l-1 4h15M7 21a1 1 0 1 1-2 0a1 1 0 0 1 2 0Zm14 0a1 1 0 1 1-2 0a1 1 0 0 1 2 0ZM13 3v3m0 0v3m0-3h3m-3 0h-3" />
                          </svg>
                        </button>
                      </div>

                      <div
                        class="bg-surface-subtle group relative flex h-52 w-full items-center justify-center overflow-hidden">
                        @let imageData = productImages.get(product.id.toString());

                        @if (imageData && imageData.isImage) {
                          <img
                            [src]="imageData.url"
                            class="h-4/5 w-4/5 object-contain transition-transform duration-700 group-hover:scale-110"
                            alt="product image" />
                        } @else {
                          <div
                            class="flex items-center justify-center transition-transform duration-500 group-hover:scale-110">
                            <svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 48 48">
                              <g class="fill-danger-text" opacity="0.8">
                                <path d="M37.084 15.735a1 1 0 1 0 .448-1.949l-6.616-1.52a1 1 0 1 0-.448 1.948z" />
                                <path
                                  fill-rule="evenodd"
                                  d="M34 22a8 8 0 1 0 0-16a8 8 0 0 0 0 16m0-2a6 6 0 1 0 0-12a6 6 0 0 0 0 12M16.778 9.245c-.647-1.532-2.813-1.686-3.682-.263L6.292 20.115c-.785 1.284.092 2.924 1.623 3.033l11.866.846c1.53.11 2.644-1.388 2.06-2.77zm-1.967.768l-.009.012L8.01 21.14l.002.002l.01.004a.1.1 0 0 0 .036.007l11.867.846h.038a.1.1 0 0 0 .029-.015l-5.056-11.96l-.004-.01a.1.1 0 0 0-.054-.014a.13.13 0 0 0-.062.009l-.003.001v.001zM35.385 36.36a6 6 0 0 0-5.071-10.876l-10.876 5.07a6 6 0 1 0 5.071 10.877zm-15.102-3.992a4 4 0 1 0 3.381 7.25l4.466-2.083l-3.38-7.25zm9.66 4.322l-3.381-7.25l4.597-2.144a4 4 0 1 1 3.38 7.25z"
                                  clip-rule="evenodd" />
                              </g>
                            </svg>
                          </div>
                        }

                        <div
                          class="absolute top-3 right-3 z-10 flex flex-col gap-2 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                          <button
                            (click)="onAddToWishlist(product)"
                            class="hover-text-primary flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-400 shadow-md transition-all">
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="1.5">
                              <path
                                d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                            </svg>
                          </button>
                          <button
                            (click)="onAddToCart(product)"
                            class="hover-text-primary flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-400 shadow-md transition-all">
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="1.5">
                              <path
                                d="M1 2h3l3 11m0 0h12l3-9M7 13l-1 4h15M7 21a1 1 0 1 1-2 0a1 1 0 0 1 2 0Zm14 0a1 1 0 1 1-2 0a1 1 0 0 1 2 0ZM13 3v3m0 0v3m0-3h3m-3 0h-3" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>

                    <div class="flex flex-1 flex-col p-4">
                      <h3 class="truncate text-[15px] font-semibold text-gray-800" [title]="product.titleEn">
                        {{ product.titleEn }}
                      </h3>

                      <div class="mt-1 flex items-center justify-between text-[11px] text-gray-400">
                        <span>{{ product.mfgCompany?.titleEn }}</span>
                        <span>EXP: {{ product.expDate || "08/27" }}</span>
                      </div>

                      <div class="my-4 h-[1px] w-full bg-gray-100"></div>

                      <div class="flex items-center justify-between">
                        <div class="flex items-center gap-2">
                          <div class="text-primary">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 4 42 42"
                              class="flex-shrink-0">
                              <g class="fill-primary">
                                <path
                                  d="M18.305 13c-.057-1.61-.011-3.315.556-4.647c.299-.7.73-1.266 1.354-1.667c.627-.402 1.527-.686 2.843-.686c1.475 0 2.704.536 3.584 1.094c.437.278.778.555 1.007.76a6 6 0 0 1 .31.3l.01.01v.001a1 1 0 0 0 1.496-1.328l-.735.65c.735-.65.733-.652.733-.652v-.001l-.004-.004l-.01-.01a3 3 0 0 0-.122-.13a8 8 0 0 0-.343-.325a10 10 0 0 0-1.27-.96C26.624 4.716 25.024 4 23.058 4c-1.606 0-2.903.349-3.923 1.003c-1.023.657-1.69 1.57-2.114 2.566c-.738 1.733-.774 3.798-.718 5.431H11c-.553 0-1.006.45-.943.998c.615 5.38 6.16 9.643 13.058 9.98V32h2v-8.034a17 17 0 0 0 4.87-1.02q.015.172.015.357c0 .762-.623 1.674-1.901 2.466a8.6 8.6 0 0 1-2.099.924v2.066a10.6 10.6 0 0 0 3.151-1.29C30.624 26.56 32 25.123 32 23.304c0-.41-.038-.815-.123-1.208c3.367-1.804 5.68-4.732 6.066-8.097c.063-.548-.391-.998-.943-.998z" />
                                <path
                                  d="M30 39c0 1.165-.36 2.188-1.019 3H32v2H16v-2h7.115v-4h2v3.914c.802-.131 1.436-.404 1.892-.763c.625-.49.993-1.196.993-2.151s-.368-1.66-.993-2.151c-.65-.512-1.662-.85-3.007-.85c-1.517 0-3.2-.119-4.531-.71c-.684-.304-1.315-.75-1.773-1.403c-.462-.66-.696-1.461-.696-2.386s.234-1.726.696-2.386c.458-.653 1.09-1.099 1.773-1.403c.76-.338 1.637-.522 2.531-.617v2.013c-.658.083-1.242.22-1.719.432c-.441.196-.747.438-.946.722c-.194.277-.335.664-.335 1.239s.14.962.335 1.239c.199.284.505.526.946.722c.92.409 2.236.539 3.719.539c1.654 0 3.144.413 4.243 1.276C29.368 36.16 30 37.455 30 39" />
                              </g>
                            </svg>
                          </div>
                          <div class="flex flex-col">
                            <span class="text-[10px] text-gray-400">Pharmacy Price</span>
                            <div class="flex items-baseline gap-1">
                              <span class="text-sm font-bold text-gray-800">{{ product.businessPrice }}</span>
                              <span class="text-[10px] font-medium text-gray-500">EGP</span>
                            </div>
                          </div>
                        </div>

                        <div class="flex items-center gap-2">
                          <div class="text-primary">
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2">
                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                              <circle cx="12" cy="7" r="4" />
                            </svg>
                          </div>
                          <div class="flex flex-col">
                            <span class="text-[10px] text-gray-400">People Price</span>
                            <div class="flex items-baseline gap-1">
                              <span class="text-sm font-bold text-gray-800">{{ product.customerPrice }}</span>
                              <span class="text-[10px] font-medium text-gray-500">EGP</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                }
              </div>

              <div class="mt-8 flex items-center justify-center gap-1">
                <button
                  (click)="changePage(currentPage() - 1)"
                  [disabled]="currentPage() === 1"
                  class="p-2 text-gray-400 disabled:opacity-20">
                  <svg
                    width="20"
                    height="20"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    viewBox="0 0 24 24"
                    class="rtl:rotate-180">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>

                @for (page of pagesArray; track page) {
                  <button
                    (click)="changePage(page)"
                    class="flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-all"
                    [class.bg-primary]="page === currentPage()"
                    [class.text-white]="page === currentPage()"
                    [class.text-gray-500]="page !== currentPage()">
                    {{ page }}
                  </button>
                }

                <button
                  (click)="changePage(currentPage() + 1)"
                  [disabled]="currentPage() === totalPages()"
                  class="p-2 text-gray-400 disabled:opacity-20">
                  <svg
                    width="20"
                    height="20"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    viewBox="0 0 24 24"
                    class="rtl:rotate-180">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </button>
              </div>
            </div>
          }
        </div>
      </div>
    </ng-template>
  </ng-template>
</div>
